import { useId, useState } from "react";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { InitialsAvatar } from "@/shared/components/ui/InitialsAvatar";
import { ItemList } from "@/shared/components/ui/ItemList";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { Section } from "@/shared/components/ui/Section";
import { useClients } from "../hooks/clientQueries";
import type { Client } from "../types/clientTypes";
import { getClientName, matchesClientSearch } from "../utils/clientDisplay";

export function ClientsList({
  selectedClients = [],
  onClientClick,
  onAddClient,
}: {
  onClientClick: (client: Client) => void;
  selectedClients?: Client[];
  onAddClient?: () => void;
}) {
  const { data: clients = [], isLoading, error, refetch } = useClients();
  const [search, setSearch] = useState("");
  const searchId = useId();
  const matches = clients
    .filter((client) => matchesClientSearch(client, search))
    .sort((a, b) => getClientName(a).localeCompare(getClientName(b)));
  const count = search.trim()
    ? `${matches.length} of ${clients.length} customers`
    : `${clients.length} ${clients.length === 1 ? "customer" : "customers"}`;

  return (
    <div className="space-y-[26px]">
      <ListSearch id={searchId} label="Search customers" placeholder="Name, email or phone" value={search} onChange={setSearch} />
      <p className="sr-only" role="status">{!isLoading && !error ? count : ""}</p>
      <Section title={search.trim() ? "Search results" : "All customers"} count={!isLoading && !error ? count : undefined}>
        {error ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : (
          <ItemList
            items={matches}
            isLoading={isLoading}
            loadingText="Loading customers..."
            emptyState={{
              title: search.trim() ? "No matching customers" : "Your first customer starts here",
              message: search.trim() ? "Try a different name, email or phone number." : "Keep contact details and appointment history together. Add a customer to get started.",
              action: search.trim() ? {
                label: "Clear search",
                variant: "outline",
                onClick: () => { setSearch(""); document.getElementById(searchId)?.focus(); },
              } : onAddClient ? { label: "Add customer", onClick: onAddClient } : undefined,
            }}
            renderItem={(client) => (
              <InfoRow
                title={getClientName(client)}
                subtitle={client.email || "No email added"}
                caption={client.phoneNumber || "No phone added"}
                density="compact"
                icon={<InitialsAvatar firstName={client.firstName || ""} lastName={client.lastName || ""} />}
                selected={selectedClients.some((selected) => selected.id === client.id)}
                searchText={search.trim()}
                onClick={() => onClientClick(client)}
              />
            )}
          />
        )}
      </Section>
    </div>
  );
}
